import { createSlice } from '@reduxjs/toolkit';
import axios from '../../axios';

// NGX (Africa/Lagos): pre-open 09:00, continuous trading 09:30-15:50, close 16:00.
// Keep in step with portfolio/market_hours.py in the backend.
const PRE_OPEN_AT = { hour: 9, minute: 0 };
const OPENS_AT = { hour: 9, minute: 30 };
const CLOSES_AT = { hour: 15, minute: 50 };
const MARKET_CLOSE_AT = { hour: 16, minute: 0 };
const toMinutes = t => t.hour * 60 + t.minute;

// Shown in "outside trading hours" messages
export const TRADING_HOURS_TEXT = '9:30am - 3:50pm, Mon - Fri excluding public holidays';

// Used only if /portfolio/market-status/ cannot be reached (e.g. before the
// backend is deployed). Uses Lagos time, not the browser's, and no holiday list.
function lagosFallbackStatus() {
	const parts = new Intl.DateTimeFormat('en-GB', {
		timeZone: 'Africa/Lagos',
		weekday: 'short',
		hour: '2-digit',
		minute: '2-digit',
		hour12: false
	}).formatToParts(new Date());
	const get = type => parts.find(p => p.type === type)?.value;
	const weekday = get('weekday');
	const minutes = Number(get('hour')) * 60 + Number(get('minute'));

	let reason = null;
	if (weekday === 'Sat' || weekday === 'Sun') reason = 'weekend';
	else if (minutes < toMinutes(PRE_OPEN_AT)) reason = 'before_open';
	else if (minutes < toMinutes(OPENS_AT)) reason = 'pre_open';
	else if (minutes > toMinutes(CLOSES_AT) && minutes < toMinutes(MARKET_CLOSE_AT)) reason = 'pre_close';
	else if (minutes >= toMinutes(MARKET_CLOSE_AT)) reason = 'after_close';

	return {
		is_open: reason === null,
		reason,
		holiday_name: null,
		now: new Date().toISOString(),
		opens_at: '09:30',
		closes_at: '15:50',
		market_close_at: '16:00',
		next_open: null,
		fallback: true
	};
}

const marketSlice = createSlice({
	name: 'market',
	initialState: {
		status: null
	},
	reducers: {
		marketStatusReceived(state, action) {
			state.status = action.payload;
		}
	}
});

export const { marketStatusReceived } = marketSlice.actions;

export const loadMarketStatus = () => async dispatch => {
	try {
		const res = await axios.get('/portfolio/market-status/');
		dispatch(marketStatusReceived(res.data));
		return res.data;
	} catch (err) {
		const status = lagosFallbackStatus();
		dispatch(marketStatusReceived(status));
		return status;
	}
};

export const selectMarketStatus = state => state.market.status;

export default marketSlice.reducer;
